import { newSpecPage } from '@stencil/core/testing';
import { NylasBookingForm } from '../nylas-booking-form';
import { InputComponent } from '../../../design-system/input-component/input-component';
import { h } from '@stencil/core';
import { RadioButtonGroup } from '../../../design-system/radio-button-group/radio-button-group';
import { CheckboxComponent } from '../../../design-system/checkbox-component/checkbox-component';
import { TextareaComponent } from '../../../design-system/textarea-component/textarea-component';
import { SelectDropdown } from '../../../design-system/select-dropdown/select-dropdown';

const configSettings = {
  scheduler: {
    available_days_in_future: 30,
    min_cancellation_notice: 0,
    min_booking_notice: 60,
    hide_rescheduling_options: false,
    hide_cancellation_options: false,
    hide_additional_guests: false,
    additional_fields: {
      additional_email: {
        label: 'Additional email',
        type: 'email',
        required: false,
        order: 2,
        default: 'additional_email@example.com',
      },
      checkbox: {
        label: 'Checkbox Label',
        type: 'checkbox',
        required: false,
        order: 6,
      },
      dropdown: {
        label: 'Dropdown Label',
        type: 'dropdown',
        required: false,
        order: 3,
        options: ['Yes', 'No'],
      },
      long_text: {
        label: 'Long text Label',
        type: 'multi_line_text',
        required: false,
        order: 5,
      },
      phone_number: {
        label: 'Phone Number',
        type: 'phone_number',
        required: false,
        order: 1,
      },
      radio_button: {
        label: 'Radio Button Label',
        type: 'radio_button',
        required: false,
        order: 7,
        options: ['Yes', 'No'],
      },
      short_text: {
        label: 'Short Text Label',
        type: 'text',
        required: false,
        order: 4,
      },
    },
    email_template: {
      booking_confirmed: {},
    },
  },
  organizer: {
    email: 'test@nylas.com',
    name: 'Test User',
  },
};

describe('nylas-booking-form', () => {
  beforeEach(() => {
    // Clear and reset the mock before each test if needed
    jest.clearAllMocks();
  });
  let consoleErrorSpy;

  beforeEach(() => {
    // Spy on console.error before each test
    consoleErrorSpy = jest.spyOn(console, 'error').mockImplementation(() => {});
  });

  afterEach(() => {
    // Restore the original console.error after each test
    consoleErrorSpy.mockRestore();
  });

  it('renders correctly with initial props', async () => {
    const bookingInfo = {
      primaryParticipant: {
        name: 'John Doe',
        email: 'john.doe@example.com',
      },
    };
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.bookingInfo = bookingInfo;
    await page.waitForChanges();
    expect(page.rootInstance.name).toEqual('John Doe');
    expect(page.rootInstance.email).toEqual('john.doe@example.com');
    expect(page.root).toMatchSnapshot();
  });

  it('renders an email error message when email is invalid', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    nameInput.value = 'John Doe';
    nameInput.dispatchEvent(new Event('input'));

    const emailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    emailInput.value = 'invalid-email';
    emailInput.dispatchEvent(new Event('input'));

    // Submit the form to trigger validation
    const form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));

    await page.waitForChanges();

    const errorMessage = page?.root?.shadowRoot
      ?.querySelector('.input-wrapper:nth-child(2) [part="nbf__input-textfield"][id="email"]')
      ?.shadowRoot?.querySelector('.help-text') as HTMLSpanElement;
    console.debug(errorMessage.className);
    expect(errorMessage.textContent).toBe('Invalid format.');
  });

  it('renders correctly with additional fields from the config', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.configSettings = configSettings;
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
    const additionalEmailInputComponent = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="additional_email"]') as HTMLInputElement;
    const additionalEmailInput = additionalEmailInputComponent.shadowRoot?.querySelector('input') as HTMLInputElement;

    expect(additionalEmailInput.value).not.toBeNull();
    expect(additionalEmailInput.value).toEqual('additional_email@example.com');
    additionalEmailInputComponent.value = 'abcd@gmail.com';

    page.root?.dispatchEvent(
      new CustomEvent('nylasFormInputChanged', {
        detail: {
          value: additionalEmailInputComponent.value,
          name: 'additional_email',
          error: '',
          label: 'Additional email',
        },
      }),
    );

    const checkboxInput = page?.root?.shadowRoot?.querySelector('[part="nbf__checkbox-component"][id="checkbox"]') as HTMLInputElement;
    checkboxInput.checked = true;

    page.root?.dispatchEvent(
      new CustomEvent('nylasFormCheckboxToggled', {
        detail: {
          checked: checkboxInput.checked,
          name: 'checkbox',
          error: '',
          label: 'Checkbox Label',
        },
      }),
    );

    await page.waitForChanges();
    expect(page.rootInstance.additionalFields).toEqual({ additional_email: 'abcd@gmail.com', checkbox: 'true' });
  });

  it('renders the values passed in the booking info by overriding the defaults', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.configSettings = configSettings;
    page.root.bookingInfo = {
      primaryParticipant: {
        name: 'John Doe',
        email: 'johndoe@example.com',
      },
      additionalFields: {
        additional_email: {
          value: 'xyz@example.com',
          type: 'email',
        },
      },
    };

    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
    const additionalEmailInputComponent = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="additional_email"]') as HTMLInputElement;
    const additionalEmailInput = additionalEmailInputComponent.shadowRoot?.querySelector('input') as HTMLInputElement;

    expect(additionalEmailInput.value).not.toBeNull();
    expect(additionalEmailInput.value).toEqual('xyz@example.com');

    await page.waitForChanges();
  });

  it('renders the fields as read only if bookingInfo passes readOnly additional fields', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent, RadioButtonGroup, CheckboxComponent, TextareaComponent, SelectDropdown],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.configSettings = configSettings;
    page.root.bookingInfo = {
      primaryParticipant: {
        name: 'John Doe',
        email: 'johndoe@example.com',
        nameReadOnly: true,
        emailReadOnly: true,
      },
      additionalFields: {
        additional_email: {
          value: 'xyz@example.com',
          type: 'email',
          readOnly: true,
        },
        checkbox: {
          value: 'true',
          type: 'checkbox',
          readOnly: true,
        },
        dropdown: {
          value: 'No',
          type: 'dropdown',
          readOnly: true,
        },
        long_text: {
          value: 'Long text Label',
          type: 'multi_line_text',
          readOnly: true,
        },
        radio_button: {
          value: 'No',
          type: 'radio_button',
          readOnly: true,
        },
      },
    };

    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
    const nameInputComponent = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]') as HTMLInputElement;
    const nameInput = nameInputComponent.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(nameInput.value).not.toBeNull();
    expect(nameInput.value).toEqual('John Doe');
    expect(nameInput).toHaveAttribute('readOnly');

    const emailInputComponent = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]') as HTMLInputElement;
    const emailInput = emailInputComponent.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(emailInput.value).not.toBeNull();
    expect(emailInput.value).toEqual('johndoe@example.com');
    expect(emailInput).toHaveAttribute('readOnly');

    const additionalEmailInputComponent = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="additional_email"]') as HTMLInputElement;
    const additionalEmailInput = additionalEmailInputComponent.shadowRoot?.querySelector('input') as HTMLInputElement;

    expect(additionalEmailInput.value).not.toBeNull();
    expect(additionalEmailInput.value).toEqual('xyz@example.com');
    expect(additionalEmailInput).toHaveAttribute('readOnly');

    const dropdown = page?.root?.shadowRoot?.querySelector('select-dropdown') as HTMLSelectDropdownElement;
    const dropdownButton = dropdown.shadowRoot?.querySelector('button') as HTMLButtonElement;
    expect(dropdownButton).toHaveAttribute('disabled');

    const checkbox = page?.root?.shadowRoot?.querySelector('checkbox-component') as HTMLCheckboxComponentElement;
    const checkboxInput = checkbox.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(checkboxInput).toHaveAttribute('disabled');
    expect(checkboxInput).toHaveAttribute('checked');

    const radioGroup = page?.root?.shadowRoot?.querySelector('radio-button-group') as HTMLRadioButtonGroupElement;
    const radioInput = radioGroup.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(radioInput).toHaveAttribute('disabled');

    const textarea = page?.root?.shadowRoot?.querySelector('textarea-component') as HTMLTextareaComponentElement;
    const textareaInput = textarea.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    expect(textareaInput).toHaveAttribute('readonly');
  });

  xit('renders guest email input on add guest button click', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const addGuestButton = page?.root?.shadowRoot?.querySelector('.add-guest') as HTMLButtonElement;
    addGuestButton.click();

    await page.waitForChanges();

    const guestEmailInputs = page?.root?.shadowRoot?.querySelectorAll('[part="nbf__input-textfield"][id^="guest-email-"]') as NodeListOf<HTMLInputElement>;
    expect(guestEmailInputs.length).toEqual(1);
  });

  xit('allows multiple guest emails to be added', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    // Simulate clicking the 'Add Guest' button twice
    const addGuestButton = page?.root?.shadowRoot?.querySelector('.add-guest') as HTMLButtonElement;
    addGuestButton.click();
    addGuestButton.click();
    await page.waitForChanges();

    // Check if two guest email inputs are present
    const guestEmailInputs = page?.root?.shadowRoot?.querySelectorAll('[id^="guest-email-"]') as NodeListOf<HTMLInputElement>;
    expect(guestEmailInputs.length).toBe(2);
  });

  xit('removes a guest email input on remove button click', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    // Add two guests then remove one
    const addGuestButton = page?.root?.shadowRoot?.querySelector('.add-guest') as HTMLButtonElement;
    addGuestButton.click();
    addGuestButton.click();
    await page.waitForChanges();

    const removeGuestButton = page?.root?.shadowRoot?.querySelector('.remove-guest') as HTMLButtonElement;
    removeGuestButton.click();
    await page.waitForChanges();

    // Check if one guest email input remains
    const guestEmailInputs = page?.root?.shadowRoot?.querySelectorAll('[id^="guest-email-"]') as NodeListOf<HTMLInputElement>;
    expect(guestEmailInputs.length).toBe(1);
  });

  it('emits nameChanged and emailChanged events on input change', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const nameChangedSpy = jest.fn();
    const emailChangedSpy = jest.fn();

    page.win.addEventListener('nameChanged', nameChangedSpy);
    page.win.addEventListener('emailChanged', emailChangedSpy);

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('label input') as HTMLInputElement;
    nameInput.value = 'John Doe';

    page.root?.dispatchEvent(
      new CustomEvent('nylasFormInputChanged', {
        detail: {
          value: nameInput.value,
          name: 'name',
          error: '',
        },
      }),
    );

    const emailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]')?.shadowRoot?.querySelector('label input') as HTMLInputElement;
    emailInput.value = 'john.doe@example.com';

    page.root?.dispatchEvent(
      new CustomEvent('nylasFormInputChanged', {
        detail: {
          value: emailInput.value,
          name: 'email',
          error: '',
        },
      }),
    );

    await page.waitForChanges();

    expect(nameChangedSpy.mock.calls[0][0].detail).toEqual('John Doe');
    expect(emailChangedSpy.mock.calls[0][0].detail).toEqual('john.doe@example.com');
  });

  it('emits backButtonClicked event on back button click', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const backButton = page?.root?.shadowRoot?.querySelector('.back') as HTMLButtonElement;
    const backButtonClickedSpy = jest.fn();
    page.win.addEventListener('backButtonClicked', backButtonClickedSpy);
    backButton.click();
    await page.waitForChanges();
    expect(backButtonClickedSpy).toHaveBeenCalled();
  });

  it('on entering name input and focusing on email input there are no errors', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    // Simulate actions

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    const emailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;

    nameInput.focus();
    nameInput.value = 'text';
    emailInput.focus();
    await page.waitForChanges();

    expect(consoleErrorSpy).not.toHaveBeenCalled();
  });

  it('validates name input correctly', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    // Simulate entering a valid name

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    nameInput.value = 'John Doe';
    nameInput.dispatchEvent(new Event('input'));
    const form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));
    await page.waitForChanges();

    // Assert no validation error message for name
    let errorMessage = page?.root?.shadowRoot?.querySelector('.input-wrapper [part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('.help-text') as HTMLSpanElement;
    expect(errorMessage).toBeNull();

    // Simulate clearing the name, triggering validation error
    nameInput.value = '';
    nameInput.dispatchEvent(new Event('input'));

    form.dispatchEvent(new Event('submit'));
    await page.waitForChanges();

    // Assert validation error message is shown for name
    errorMessage = page?.root?.shadowRoot?.querySelector('.input-wrapper [part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('.help-text') as HTMLSpanElement;
    expect(errorMessage.textContent).toBe('This field is required.');
  });

  it('validates email input correctly', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('label input[name="name"]') as HTMLInputElement;
    nameInput.value = 'John Doe';
    nameInput.dispatchEvent(new Event('input'));

    // Simulate entering a valid email
    const emailInput = page?.root?.shadowRoot
      ?.querySelector('[part="nbf__input-textfield"][id="email"]')
      ?.shadowRoot?.querySelector('label input[name="email"]') as HTMLInputElement;
    emailInput.value = 'john.doe@example.com';
    emailInput.dispatchEvent(new Event('input'));

    let form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));
    await page.waitForChanges();

    // Assert no validation error message for email
    let errorMessage = page?.root?.shadowRoot
      ?.querySelector('.input-wrapper:nth-child(2) [part="nbf__input-textfield"][id="email"]')
      ?.shadowRoot?.querySelector('.help-text') as HTMLSpanElement;
    await page.waitForChanges();

    expect(errorMessage).toBeNull();

    // Simulate clearing the email, triggering validation error
    emailInput.value = '';
    emailInput.dispatchEvent(new Event('input'));
    await page.waitForChanges();
    form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));
    await page.waitForChanges();

    // Assert validation error message is shown for empty email
    errorMessage = page?.root?.shadowRoot
      ?.querySelector('.input-wrapper:nth-child(2) [part="nbf__input-textfield"][id="email"]')
      ?.shadowRoot?.querySelector('.help-text') as HTMLSpanElement;
    expect(errorMessage.textContent).toBe('This field is required.');
  });

  xit('validates guest email inputs correctly', async () => {
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent],
      html: `<nylas-booking-form></nylas-booking-form>`,
    });

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    nameInput.value = 'John Doe';
    nameInput.dispatchEvent(new Event('input'));

    const emailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    emailInput.value = 'john.doe@example.com';
    emailInput.dispatchEvent(new Event('input'));

    // Simulate adding a guest
    const addGuestButton = page?.root?.shadowRoot?.querySelector('.add-guest') as HTMLButtonElement;
    addGuestButton.click();
    await page.waitForChanges();

    const guestEmailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id^="guest-email-0"]') as HTMLInputElement;

    // Simulate on blur event to trigger validation
    guestEmailInput.dispatchEvent(new Event('blur'));
    await page.waitForChanges();

    // Assert validation error message is shown for empty guest email
    const errorMessage = page?.root?.shadowRoot?.querySelector('.input-wrapper:nth-child(3) .button-wrapper .help-text') as HTMLParagraphElement;
    expect(errorMessage.textContent).toBe('Email is required.');

    // Simulate entering an ivalid guest email
    guestEmailInput.value = 'invalid-email';
    guestEmailInput.dispatchEvent(new Event('input'));
    const form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));
    await page.waitForChanges();

    // Assert validation error message is shown for invalid guest email
    expect(errorMessage.textContent).toBe('Invalid format. ');
  });

  it('prefills rescheduling form with the correct data', async () => {
    const rescheduleConfigSettings = {
      name: '',
      configuration_id: '4115ac80-d606-4b64-98b8-9c0420c8f318',
      slug: 'test-slug',
      scheduler: {
        available_days_in_future: 58,
        min_cancellation_notice: 0,
        min_booking_notice: 60,
        rescheduling_url: 'https://book.nylas.com/reschedule/:booking_ref',
        cancellation_url: 'https://book.nylas.com/cancel/:booking_ref',
        organizer_confirmation_url: 'https://book.nylas.com/confirm/:booking_ref',
        confirmation_redirect_url: '',
        hide_rescheduling_options: false,
        hide_cancellation_options: false,
        hide_additional_guests: false,
        cancellation_policy: '',
        additional_fields: {
          'metadata_Metadata-Label_1': {
            label: 'Metadata Label',
            type: 'metadata',
            required: false,
            default: 'xyz',
            order: 1,
          },
          'phone_number_Phone-Number-Label_1': {
            label: 'Phone Number Label',
            type: 'phone_number',
            required: false,
            default: '9964646442',
            order: 2,
          },
          'multi-select': {
            default: 'orange',
            label: 'Select favorite fruits',
            order: 3,
            required: true,
            type: 'multi_select',
            options: ['orange', 'apple', 'banana', 'kiwi'],
          },
          'multi_line_text_Long-text-Label_1': {
            label: 'Long text Label',
            type: 'multi_line_text',
            required: true,
            order: 4,
          },
        },
        email_template: {
          logo: '',
          booking_confirmed: {},
        },
      },
      organizer: {
        email: 'test@nylas.com',
        name: 'Test User',
      },
      booking_type: 'booking',
      booking: {
        participants: [
          {
            email: 'test@nylas.com',
            is_organizer: true,
            name: 'Test User',
            availability: {
              calendar_ids: ['test@nylas.com'],
            },
            booking: {
              calendar_id: 'test@nylas.com',
            },
            timezone: 'America/Toronto',
          },
        ],
        guest: {
          name: 'Dalmia Fanning',
          email: 'dalmia@company.com',
        },
        timezone: 'America/Toronto',
        email_language: 'en',
        cancellation_reason: '',
        additional_guests: [
          {
            email: 'dale@fur.com',
            name: 'Dale Fur',
          },
        ],
        additional_fields: {
          'metadata_Metadata-Label_1': 'xyz',
          'phone_number_Phone-Number-Label_1': '1234567890',
        },
        booking_ref: 'test-booking-ref',
        booking_ref_salt: '',
        event_data: {
          when: {
            start_timezone: 'America/Toronto',
            end_timezone: 'America/Toronto',
            object: 'timespan',
            start_time: new Date(1730952000 * 1000),
            end_time: new Date(1730953800 * 1000),
          },
          location: '',
        },
        event_organizer: {
          email: 'test@nylas.com',
          is_organizer: true,
          name: 'Test User',
          availability: {
            calendar_ids: ['test@nylas.com'],
          },
          booking: {
            calendar_id: 'test@nylas.com',
          },
          timezone: 'America/Toronto',
        },
      },
      appearance: {},
    };

    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent, TextareaComponent],
      template: () => <nylas-booking-form configSettings={rescheduleConfigSettings} />,
    });

    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();

    const guestEmailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="guest-email-0"]') as HTMLInputElement;
    expect(guestEmailInput.value).toEqual('dale@fur.com');

    const inputComponent = page?.root?.shadowRoot?.querySelector('input-component#phone_number_Phone-Number-Label_1')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(inputComponent.value).toEqual('1234567890');

    const spyEvent = jest.fn();
    page.win.addEventListener('detailsConfirmed', spyEvent);

    const form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));

    await page.waitForChanges();

    const textAreaComponent = page?.root?.shadowRoot?.querySelector('textarea-component#multi_line_text_Long-text-Label_1') as HTMLTextareaComponentElement;
    const textArea = textAreaComponent.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    console.log('###textAreaComponent:', textArea);

    textArea.value = 'This is a long text';
    textArea.dispatchEvent(new Event('input'));

    const event = new CustomEvent('nylasFormInputChanged', {
      detail: {
        value: textArea.value,
        name: 'multi_line_text_Long-text-Label_1',
        error: '',
        label: 'Long text Label',
      },
    });
    (page.rootInstance as unknown as NylasBookingForm).nylasFormInputChangedHandler(event);
    await page.waitForChanges();

    form.dispatchEvent(new Event('submit'));

    await page.waitForChanges();

    expect(spyEvent).toHaveBeenCalled();
    const obj = {
      additionalFields: {
        'metadata_Metadata-Label_1': 'xyz',
        'phone_number_Phone-Number-Label_1': '1234567890',
        'multi-select': 'orange',
        'multi_line_text_Long-text-Label_1': 'This is a long text',
      },
      guests: [
        {
          email: 'dale@fur.com',
          name: 'dale@fur.com',
        },
      ],
      primaryParticipant: {
        email: 'dalmia@company.com',
        name: 'Dalmia Fanning',
      },
    };
    expect(spyEvent.mock.calls[0][0].detail).toEqual(obj);
  });

  it('reschedule flow: overrides the booking data from the config with the booking info passed', async () => {
    const rescheduleConfigSettings = {
      name: '',
      configuration_id: '4115ac80-d606-4b64-98b8-9c0420c8f318',
      slug: 'test-slug',
      scheduler: {
        available_days_in_future: 58,
        min_cancellation_notice: 0,
        min_booking_notice: 60,
        rescheduling_url: 'https://book.nylas.com/reschedule/:booking_ref',
        cancellation_url: 'https://book.nylas.com/cancel/:booking_ref',
        organizer_confirmation_url: 'https://book.nylas.com/confirm/:booking_ref',
        confirmation_redirect_url: '',
        hide_rescheduling_options: false,
        hide_cancellation_options: false,
        hide_additional_guests: false,
        cancellation_policy: '',
        additional_fields: {
          'metadata_Metadata-Label_1': {
            label: 'Metadata Label',
            type: 'metadata',
            required: false,
            default: 'xyz',
            order: 1,
          },
          'phone_number_Phone-Number-Label_1': {
            label: 'Phone Number Label',
            type: 'phone_number',
            required: false,
            default: '9964646442',
            order: 2,
          },
          'multi-select': {
            default: 'orange',
            label: 'Select favorite fruits',
            order: 3,
            required: true,
            type: 'multi_select',
            options: ['orange', 'apple', 'banana', 'kiwi'],
          },
          'multi_line_text_Long-text-Label_1': {
            label: 'Long text Label',
            type: 'multi_line_text',
            required: true,
            order: 4,
          },
        },
        email_template: {
          logo: '',
          booking_confirmed: {},
        },
      },
      organizer: {
        email: 'test@nylas.com',
        name: 'Test User',
      },
      booking_type: 'booking',
      booking: {
        participants: [
          {
            email: 'test@nylas.com',
            is_organizer: true,
            name: 'Test User',
            availability: {
              calendar_ids: ['test@nylas.com'],
            },
            booking: {
              calendar_id: 'test@nylas.com',
            },
            timezone: 'America/Toronto',
          },
        ],
        guest: {
          name: 'Dalmia Fanning',
          email: 'dalmia@company.com',
        },
        timezone: 'America/Toronto',
        email_language: 'en',
        cancellation_reason: '',
        additional_guests: [
          {
            email: 'dale@fur.com',
            name: 'Dale Fur',
          },
        ],
        additional_fields: {
          'metadata_Metadata-Label_1': 'xyz',
          'phone_number_Phone-Number-Label_1': '1234567890',
          'multi-select': 'apple',
        },
        booking_ref: 'test-booking-ref',
        booking_ref_salt: '',
        event_data: {
          when: {
            start_timezone: 'America/Toronto',
            end_timezone: 'America/Toronto',
            object: 'timespan',
            start_time: new Date(1730952000 * 1000),
            end_time: new Date(1730953800 * 1000),
          },
          location: '',
        },
        event_organizer: {
          email: 'test@nylas.com',
          is_organizer: true,
          name: 'Test User',
          availability: {
            calendar_ids: ['test@nylas.com'],
          },
          booking: {
            calendar_id: 'test@nylas.com',
          },
          timezone: 'America/Toronto',
        },
      },
      appearance: {},
    };
    const bookingInfo = {
      primaryParticipant: {
        name: 'Dale Fur',
        email: 'dale@nylas.com',
      },
      additionalFields: {
        'metadata_Metadata-Label_1': {
          value: 'abc',
        },
        'phone_number_Phone-Number-Label_1': {
          value: '3111111111',
          readOnly: true,
        },
      },
    };
    const page = await newSpecPage({
      components: [NylasBookingForm, InputComponent, TextareaComponent],
      template: () => <nylas-booking-form configSettings={rescheduleConfigSettings} />,
    });
    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.bookingInfo = bookingInfo;
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();

    const nameInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="name"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(nameInput.value).toEqual('Dale Fur');

    const emailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="email"]')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(emailInput.value).toEqual('dale@nylas.com');

    const guestEmailInput = page?.root?.shadowRoot?.querySelector('[part="nbf__input-textfield"][id="guest-email-0"]') as HTMLInputElement;
    expect(guestEmailInput.value).toEqual('dale@fur.com');

    const inputComponent = page?.root?.shadowRoot?.querySelector('input-component#phone_number_Phone-Number-Label_1')?.shadowRoot?.querySelector('input') as HTMLInputElement;
    expect(inputComponent.value).toEqual('3111111111');
    expect(inputComponent).toHaveAttribute('readonly');

    const spyEvent = jest.fn();
    page.win.addEventListener('detailsConfirmed', spyEvent);

    const form = page?.root?.shadowRoot?.querySelector('form') as HTMLFormElement;
    form.dispatchEvent(new Event('submit'));

    await page.waitForChanges();

    const textAreaComponent = page?.root?.shadowRoot?.querySelector('textarea-component#multi_line_text_Long-text-Label_1') as HTMLTextareaComponentElement;
    const textArea = textAreaComponent.shadowRoot?.querySelector('textarea') as HTMLTextAreaElement;
    console.log('###textAreaComponent:', textArea);

    textArea.value = 'This is a long text';
    textArea.dispatchEvent(new Event('input'));

    const event = new CustomEvent('nylasFormInputChanged', {
      detail: {
        value: textArea.value,
        name: 'multi_line_text_Long-text-Label_1',
        error: '',
        label: 'Long text Label',
      },
    });
    (page.rootInstance as unknown as NylasBookingForm).nylasFormInputChangedHandler(event);
    await page.waitForChanges();

    form.dispatchEvent(new Event('submit'));

    await page.waitForChanges();

    expect(spyEvent).toHaveBeenCalled();
    const obj = {
      additionalFields: {
        'metadata_Metadata-Label_1': 'abc',
        'phone_number_Phone-Number-Label_1': '3111111111',
        'multi-select': 'apple',
        'multi_line_text_Long-text-Label_1': 'This is a long text',
      },
      guests: [
        {
          email: 'dale@fur.com',
          name: 'dale@fur.com',
        },
      ],
      primaryParticipant: {
        email: 'dale@nylas.com',
        name: 'Dale Fur',
      },
    };
    expect(spyEvent.mock.calls[0][0].detail).toEqual(obj);
  });
});
